import type { HealItem, Item } from '../../shared/items.ts';
import { itemCatalog, itemVisual } from '../common/item-catalog.ts';
import { dominantElement, effectivenessAgainst, elementTypes, SAME_TYPE_BONUS, typesOf, type Element as ElementKey, type Typed } from '../../shared/elements.ts';
import { describeEffect, ENERGY_MAX, MAX_MOVES, restEnergy, type Move } from '../../shared/moves.ts';
import {
  MAX_FIELD_PLACES, type BadgeView, type BattleAction, type BattleAlly, type BattleEnd, type BattleSnapshot, type BattleStep, type EvolutionView, type Inventory, type MoveOffer,
  type OwnedMonsterView, type WildMonsterView, type XpGain,
} from '../../shared/protocol.ts';
import { $, $$, el, restartAnimation, shortcutLetter } from '../common/dom.ts';
import { icon, withIcon } from '../common/icons.ts';
import { BattleStage, type Slot } from './battle-stage.ts';
import { LootReveal } from './loot-reveal.ts';
import { Pacer, SequenceCancelled } from './pacer.ts';
import type { SoundBoard } from './sound.ts';
import { creatureText } from '../../shared/game-config.ts';

/** Touches 1 à 4 : les attaques, dans l'ordre. */
const MOVE_KEYS: Readonly<Record<string, number>> = {
  Digit1: 0, Digit2: 1, Digit3: 2, Digit4: 3, Numpad1: 0, Numpad2: 1, Numpad3: 2, Numpad4: 3,
};
/** Durée de l'effet d'entrée en combat avant la première étape. */
const INTRO_MS = 500;
/** Écran « VS » des combats contre un dresseur ou un joueur. */
const VERSUS_MS = 1300;
/** Temps laissé à un objet laissé par un monstre, au milieu de l'écran, avant l'étape suivante (il s'efface ensuite de lui-même). */
const LOOT_HOLD_MS = 1600;

/**
 * Aperçu de l'expérience de l'équipe en fin de combat : ouverture, remplissage des barres (montées de niveau comprises),
 * décalé d'une carte à la suivante.
 */
const XP_PANEL = { openMs: 250, fillMs: 900, staggerMs: 120 } as const;

/** Part (en %) de l'expérience du niveau en cours. */
const xpPercent = (xp: number, xpNext: number): number => Math.min(100, (100 * xp) / Math.max(1, xpNext));

/** Cases du menu des attaques : les attaques connues et le Repos. */
const MOVE_SLOTS = MAX_MOVES + 1;

/**
 * Cases vides, invisibles, qui complètent le menu des attaques : il garde la même hauteur quel que soit le nombre
 * d'attaques connues (et le panneau du bas, celle qu'il a entre deux tours).
 */
function emptyMoveSlots(count: number): HTMLElement[] {
  return Array.from({ length: Math.max(0, count) }, () => {
    const slot = el('button', { className: 'empty-slot', disabled: true, tabIndex: -1 },
      el('strong', { textContent: '·' }), el('span', { className: 'pp', textContent: '·' }), el('small', { textContent: '·' }));
    slot.setAttribute('aria-hidden', 'true');
    return slot;
  });
}

/** Un objet de soin lui ferait du bien : ni K.O., ni en pleine forme. */
const canHeal = (monster: OwnedMonsterView): boolean => monster.hp > 0 && monster.hp < monster.maxHp;

interface Choice {
  label: string;
  detail?: string;
  image?: string;
  /** Objet représenté (son image ou son icône). */
  item?: Item;
  /** Indice affiché après le nom (attaque super efficace…), avec son explication au survol. */
  hint?: { label: (Node | string)[]; title: string };
  /** Contour coloré de la case (choix de la cible : efficacité de l'attaque contre elle), expliqué au survol. */
  tone?: { className: string; title: string };
  disabled?: boolean;
  onPick: () => void;
}

/** Ce dont les étapes ont besoin pour trouver les monstres à afficher. */
interface StepContext {
  /** Monstres sur le terrain à la fin de la séquence, par place. */
  foes: readonly (WildMonsterView | null)[];
  allies: readonly (OwnedMonsterView | null)[];
  /** Monstres de l'équipe à jour (fin de combat : expérience, niveau). */
  collection: readonly OwnedMonsterView[];
  /** Dernière séquence d'un combat gagné : le dernier K.O. adverse est celui qui l'emporte. */
  final?: boolean;
}

/** La fiche d'un allié sur le terrain, PV du combat compris. */
const allyMonster = (ally: BattleAlly | null | undefined): OwnedMonsterView | null => ally?.monster ?? null;

export interface BattleScreenHandlers {
  onAction(action: BattleAction): void;
  /** Appelé en quittant l'écran de fin de combat, avec l'évolution et le badge éventuels à mettre en scène. */
  onClosed(evolutions: readonly EvolutionView[], badge: BadgeView | null, newMoves: readonly MoveOffer[]): void;
}

/**
 * Écran de combat (sauvage ou duel). Chaque message du serveur est rejoué comme une séquence :
 * tirage, puis étapes une à une (texte lettre par lettre et animation associée). Les séquences
 * s'enchaînent dans l'ordre ; les actions ne réapparaissent qu'une fois la séquence terminée.
 * Un clic sur l'écran (ou Espace / Entrée) accélère la séquence en cours.
 */
export class BattleScreen {
  private readonly root = $('#battle');
  private readonly actions = $('.actions', this.root);
  private readonly moveList = $('.moves', this.root);
  /** Catalogue des attaques (reçu à la connexion), pour afficher noms, types et coûts. */
  private catalog: ReadonlyMap<number, Move> = new Map();
  private readonly choices = $('.choice-list', this.root);
  private readonly waiting = $('.waiting', this.root);
  /** Combat double : quel monstre attend son ordre, et retour au précédent. */
  private readonly prompt = el('p', { className: 'turn-prompt', hidden: true });
  /** Place du terrain dont le joueur choisit l'action en ce moment. */
  private place = 0;
  private readonly skipHint = $('.skip-hint', this.root);
  private readonly continueButton = $<HTMLButtonElement>('.continue', this.root);
  private readonly versus = el('div', { className: 'versus' });
  /** Aperçu de fin de combat : l'expérience gagnée par chaque monstre de l'équipe. */
  private readonly xpPanel = el('div', { className: 'xp-panel' });
  /** Nom du joueur, pour l'écran « VS ». */
  private playerName = 'Vous';
  private readonly handlers: BattleScreenHandlers;
  private readonly sounds: SoundBoard;
  private readonly pacer = new Pacer();
  private readonly stage: BattleStage;
  /** Objets laissés par les monstres vaincus, au milieu de l'écran (au-dessus du combat). */
  private readonly lootReveal: LootReveal;
  private queue: Promise<void> = Promise.resolve();
  private playing = false;
  private state: BattleSnapshot | null = null;
  private inventory: Inventory | null = null;
  private pendingEvolutions: EvolutionView[] = [];
  private pendingBadge: BadgeView | null = null;
  /** Attaques à proposer à la fin du combat (monstres qui en connaissent déjà le maximum). */
  private pendingMoves: MoveOffer[] = [];

  constructor(handlers: BattleScreenHandlers, sounds: SoundBoard) {
    this.handlers = handlers;
    this.sounds = sounds;
    this.stage = new BattleStage(this.root, sounds, this.pacer);
    this.lootReveal = new LootReveal(this.root);
    this.versus.hidden = true;
    this.xpPanel.hidden = true;
    this.root.append(this.versus, this.xpPanel);
    this.actions.before(this.prompt);
    if (document.body.classList.contains('touch')) this.skipHint.textContent = 'Touchez l\'écran pour accélérer';
    this.actions.addEventListener('click', (event) => this.onActionClick(event));
    this.continueButton.addEventListener('click', () => this.close());
    this.root.addEventListener('click', (event) => {
      if (this.playing && !(event.target as Element).closest('button')) this.pacer.hurry();
    });
  }

  /** Arrêt sur image en cours (le monde se fige avec l'arène). */
  get frozen(): boolean {
    return this.stage.isFrozen;
  }

  /** Couleur du type du monstre à chaque place (« ally0 », « foe1 »…), null pour une place vide. */
  slotColors(): Record<string, string | null> {
    const colors: Record<string, string | null> = {};
    for (const target of ['ally', 'foe'] as const) {
      for (let place = 0; place < MAX_FIELD_PLACES; place++) {
        const element = this.stage.elementOf({ target, place });
        colors[`${target}${place}`] = element ? elementTypes.get(element).color : null;
      }
    }
    return colors;
  }

  /** Vrai tant que l'écran est affiché, y compris sur l'écran de fin. */
  get isOpen(): boolean {
    return !this.root.hidden;
  }

  setMoveCatalog(catalog: ReadonlyMap<number, Move>): void {
    this.catalog = catalog;
  }

  setPlayerName(name: string): void {
    this.playerName = name;
  }

  setInventory(inventory: Inventory): void {
    this.inventory = inventory;
  }

  open(snapshot: BattleSnapshot): void {
    this.pacer.cancel();
    this.queue = Promise.resolve();
    this.state = null;
    this.pendingEvolutions = [];
    this.pendingBadge = null;
    this.pendingMoves = [];
    this.stage.reset();
    this.stage.setPlaces(snapshot.allies.length, snapshot.foes.length);
    this.stage.setWeather(snapshot.weather);
    this.stage.setFoeTeam(snapshot.foeTeam);
    this.root.hidden = false;
    this.root.classList.toggle('duel-mode', snapshot.mode === 'duel');
    this.continueButton.hidden = true;
    this.xpPanel.hidden = true;
    // Le menu des attaques a toujours la même hauteur, même avant le premier choix : le panneau ne bouge pas.
    this.moveList.replaceChildren(...emptyMoveSlots(MOVE_SLOTS));
    this.lockActions();
    restartAnimation(this.root, 'intro');
    this.sounds.play(snapshot.mode === 'duel' ? 'duel' : 'encounter');
    const versus = snapshot.mode !== 'wild';
    if (versus) {
      this.versus.replaceChildren(el('span', { className: 'me' }, this.playerName), el('strong', {}, 'VS'), el('span', { className: 'them' }, snapshot.opponent ?? '?'));
      // Chaque camp à la couleur du type dominant de son équipe.
      const color = (element: ElementKey | null) => (element ? elementTypes.get(element).color : '');
      this.versus.style.setProperty('--versus-me', color(dominantElement(snapshot.team)) || null);
      this.versus.style.setProperty('--versus-them', color(snapshot.foeTeam?.element ?? null) || null);
      this.versus.hidden = false;
      restartAnimation(this.versus, 'show');
    }
    this.enqueue(async () => {
      try {
        await this.pacer.wait(INTRO_MS + (versus ? VERSUS_MS : 0));
      } finally {
        this.versus.hidden = true;
      }
      await this.playTurn(snapshot);
    });
  }

  update(snapshot: BattleSnapshot): void {
    this.enqueue(() => this.playTurn(snapshot));
  }

  end(end: BattleEnd): void {
    this.enqueue(() => this.playEnd(end));
  }

  close(): void {
    if (this.root.hidden) return;
    this.pacer.cancel();
    this.stage.calm();
    this.root.hidden = true;
    this.xpPanel.hidden = true;
    this.state = null;
    const { pendingEvolutions: evolutions, pendingBadge: badge, pendingMoves: newMoves } = this;
    this.pendingEvolutions = [];
    this.pendingBadge = null;
    this.pendingMoves = [];
    this.handlers.onClosed(evolutions, badge, newMoves);
  }

  /** Clavier : 1 à 4 pour attaquer, Espace/Entrée pour accélérer ; renvoie vrai si la touche a servi. */
  handleKey(event: KeyboardEvent): boolean {
    if (this.playing && (event.code === 'Space' || event.key === 'Enter')) {
      event.preventDefault();
      this.pacer.hurry();
      return true;
    }
    const slot = MOVE_KEYS[event.code];
    const button = shortcutLetter(event) === 'r' || event.code === 'Digit5' ? this.actions.querySelector<HTMLButtonElement>('[data-act="rest"]')
      : slot === undefined ? null
      : this.actions.querySelector<HTMLButtonElement>(`[data-slot="${slot}"]`) ?? (slot === 0 ? this.actions.querySelector<HTMLButtonElement>('[data-slot="-1"]') : null);
    if (!button || button.disabled || this.actions.hidden) return false;
    button.click();
    return true;
  }

  // ---------- Séquences ----------

  private enqueue(sequence: () => Promise<void>): void {
    this.queue = this.queue.then(async () => {
      this.playing = true;
      this.skipHint.hidden = false;
      try {
        await sequence();
      } catch (error) {
        if (!(error instanceof SequenceCancelled)) throw error;
      } finally {
        this.playing = false;
        this.skipHint.hidden = true;
        this.pacer.normal();
      }
    });
  }

  private async playTurn(snapshot: BattleSnapshot): Promise<void> {
    this.lockActions();
    await this.playSteps(snapshot.steps, { foes: snapshot.foes, allies: snapshot.allies.map(allyMonster), collection: snapshot.team });
    this.state = snapshot;
    this.stage.setFoeTeam(snapshot.foeTeam);
    snapshot.foes.forEach((foe, place) => {
      const slot: Slot = { target: 'foe', place };
      this.stage.sync(slot, foe);
      this.stage.setStatus(slot, foe?.status ?? null);
      if (foe) this.stage.setEnergy(slot, foe.energy);
    });
    snapshot.allies.forEach((ally, place) => {
      const slot: Slot = { target: 'ally', place };
      this.stage.sync(slot, allyMonster(ally));
      this.stage.setStatus(slot, ally?.status ?? null);
      if (ally) this.stage.setEnergy(slot, ally.energy);
    });
    this.showActions(snapshot);
  }

  private async playEnd({ result, steps, rewards, collection }: BattleEnd): Promise<void> {
    this.lockActions();
    const state = this.state;
    await this.playSteps(steps, { foes: state?.foes ?? [], allies: state?.allies.map(allyMonster) ?? [], collection, final: result === 'win' });

    if (result === 'win') this.sounds.play(rewards?.levelUps ? 'levelup' : 'victory');
    else if (result === 'lose') this.sounds.play('defeat');
    this.pendingEvolutions = rewards?.evolutions ?? [];
    this.pendingBadge = rewards?.badge ?? null;
    this.pendingMoves = rewards?.newMoves ?? [];
    if (rewards?.team.length) await this.showXpPanel(rewards.team, collection);
    this.continueButton.textContent = this.pendingEvolutions.length ? 'Hein ? Il se passe quelque chose…' : 'Continuer';
    this.continueButton.hidden = false;
    this.continueButton.focus();
  }

  /**
   * Bande de l'équipe, posée sur la carte au-dessus du joueur : pour chaque monstre, une petite carte (portrait, niveau)
   * dont la barre d'expérience part d'avant le combat et se remplit jusqu'à maintenant, un « +N » s'envolant au-dessus.
   * Les cartes se remplissent l'une après l'autre, de peu, pour que l'œil suive.
   */
  private async showXpPanel(gains: readonly XpGain[], collection: readonly OwnedMonsterView[]): Promise<void> {
    const cards = gains.flatMap((gain, index) => {
      const monster = collection.find((candidate) => candidate.id === gain.monsterId);
      if (!monster) return [];
      const level = el('span', { className: 'lvl', textContent: `Nv ${gain.level}` });
      const fill = el('div');
      fill.style.width = `${xpPercent(gain.xp, gain.xpNext)}%`;
      const bar = el('div', { className: 'bar xp' }, fill);
      const plus = el('span', { className: 'xp-gain', textContent: `+${gain.gained}` });
      const card = el('div', { className: `xp-card${monster.hp <= 0 ? ' fainted' : ''}`, title: `${monster.name} : +${gain.gained} XP` },
        el('img', { src: monster.image, alt: monster.name }), level, bar, plus);
      card.style.setProperty('--i', String(index));
      const play = async () => {
        await this.pacer.wait(index * XP_PANEL.staggerMs);
        restartAnimation(plus, 'fly');
        bar.classList.add('filling');
        try {
          await this.fillXp(fill, level, gain, monster);
        } finally {
          bar.classList.remove('filling');
        }
      };
      return [{ card, play }];
    });
    if (!cards.length) return;
    this.xpPanel.replaceChildren(...cards.map(({ card }) => card));
    this.xpPanel.hidden = false;
    restartAnimation(this.xpPanel, 'show');
    await this.pacer.wait(XP_PANEL.openMs);
    await Promise.all(cards.map(({ play }) => play()));
  }

  /** Remplit une barre d'expérience : jusqu'au bout et on repart de zéro à chaque niveau gagné, puis jusqu'à l'expérience actuelle. */
  private async fillXp(fill: HTMLElement, label: HTMLElement, gain: XpGain, now: OwnedMonsterView): Promise<void> {
    const card = label.closest<HTMLElement>('.xp-card');
    const levels = Math.max(0, now.level - gain.level);
    const duration = XP_PANEL.fillMs / (levels + 1);
    let from = xpPercent(gain.xp, gain.xpNext);
    for (let step = 0; step <= levels; step++) {
      const to = step < levels ? 100 : xpPercent(now.xp, now.xpNext);
      await fill.animate([{ width: `${from}%` }, { width: `${to}%` }], { duration, easing: 'ease-out' }).finished.catch(() => undefined);
      fill.style.width = `${to}%`;
      if (step === levels) break;
      label.textContent = `Nv ${gain.level + step + 1}`;
      restartAnimation(label, 'up');
      if (card) restartAnimation(card, 'leveled');
      this.sounds.play('levelup');
      from = 0;
    }
  }

  private async playSteps(steps: BattleStep[], context: StepContext): Promise<void> {
    for (let i = 0; i < steps.length; i++) await this.playStep(steps[i]!, context, steps.slice(i + 1));
  }

  /**
   * Le texte s'écrit, puis l'effet est joué ; l'étape suivante attend la fin des deux. Les étapes à venir disent
   * comment mettre en scène celle-ci (une attaque esquivée, une barre d'expérience qui va déborder…).
   */
  private async playStep({ text, effect }: BattleStep, context: StepContext, upcoming: BattleStep[]): Promise<void> {
    const { stage } = this;
    // Une étape sans texte ne fait que jouer son effet (statut qui change…). « X utilise Y ! » n'attend pas de pause
    // de lecture : l'étape suivante (le coup) arrive à l'image d'impact de l'animation, la ligne reste affichée.
    const said = text ? stage.say(text, effect?.type === 'move' ? 0 : undefined) : Promise.resolve();
    // Expérience d'un monstre allié sur le terrain : sa fiche à jour, et sa place.
    const progress = (monsterId: number) => {
      const slot = stage.allyOf(monsterId);
      const monster = context.collection.find((candidate) => candidate.id === monsterId);
      return slot && monster ? { slot, monster } : null;
    };
    const more = (monsterId: number, type: 'xp' | 'levelup') => upcoming.some((step) => step.effect?.type === type && step.effect.monsterId === monsterId);
    switch (effect?.type) {
      case undefined:
        break;
      case 'appear': {
        const monster = (effect.target === 'foe' ? context.foes : context.allies)[effect.place];
        if (monster) await stage.appear(effect, monster, effect.hp, effect.stages);
        break;
      }
      case 'damage':
        await stage.damage(effect, effect.hp, effect.effectiveness, effect.critical, effect.weather);
        break;
      case 'heal':
        await stage.heal(effect, effect.hp);
        break;
      case 'faint': {
        // Le K.O. qui gagne le combat : le dernier adversaire à tomber.
        const last = context.final === true && effect.target === 'foe' && !upcoming.some((step) => step.effect?.type === 'faint' && step.effect.target === 'foe');
        await stage.faint(effect, last);
        break;
      }
      case 'move': {
        stage.spendEnergy(effect, effect.energy);
        const next = upcoming[0]?.effect?.type;
        await stage.attack(effect, effect.element, effect.power, effect.animation, next === 'dodge' || next === 'immune' ? next : 'hit', effect.aim);
        break;
      }
      case 'dodge':
      case 'immune':
        // Déjà mis en scène avec l'attaque.
        break;
      case 'rest':
        await stage.rest(effect, effect.energy);
        break;
      case 'stage':
        await stage.statChange(effect, effect.rise, effect.stat, effect.stages);
        break;
      case 'status':
        if (effect.status) await stage.afflict(effect, effect.status);
        else stage.setStatus(effect, null);
        break;
      case 'flee':
        await stage.flee(effect.target);
        break;
      case 'capsule':
        await said; // on lit « Vous lancez une capsule… » avant le lancer
        await stage.throwCapsule(effect.place, effect.item, effect.shakes, effect.caught);
        return;
      case 'xp': {
        // Avec une montée de niveau à suivre, la barre se remplit jusqu'au bout ; sinon jusqu'à l'expérience gagnée.
        const shown = progress(effect.monsterId);
        if (shown) await stage.gainXp(shown.slot, shown.monster, more(effect.monsterId, 'levelup') ? 100 : undefined);
        if (!upcoming.some((step) => step.effect?.type === 'levelup' || step.effect?.type === 'xp')) stage.hideXp();
        break;
      }
      case 'levelup': {
        const shown = progress(effect.monsterId);
        if (shown) {
          await stage.levelUp(shown.slot, effect.level);
          await stage.gainXp(shown.slot, shown.monster, more(effect.monsterId, 'levelup') ? 100 : undefined);
        }
        if (!upcoming.some((step) => step.effect?.type === 'levelup' || step.effect?.type === 'xp')) stage.hideXp();
        break;
      }
      case 'coins':
        if (effect.amount > 0) {
          this.sounds.play('coin');
          await stage.flyCoins(effect.amount);
        }
        break;
      case 'badge':
        await stage.showBadge(effect.icon, effect.color);
        break;
      case 'loot':
        this.sounds.play('levelup');
        this.lootReveal.show({ items: [{ item: effect.item, quantity: effect.quantity }], coins: 0 });
        await this.pacer.wait(LOOT_HOLD_MS);
        break;
    }
    await said;
  }

  // ---------- Actions ----------

  private lockActions(): void {
    this.actions.hidden = true;
    this.choices.hidden = true;
    this.waiting.hidden = true;
    this.prompt.hidden = true;
    this.stage.highlight(null);
  }

  /** Allié à cette place du terrain (null : place vide ou monstre K.O.). */
  private allyAt(place: number): OwnedMonsterView | null {
    const monster = allyMonster(this.state?.allies[place]);
    return monster && monster.hp > 0 ? monster : null;
  }

  /** Places adverses dont le monstre peut être visé. */
  private targets(): number[] {
    return (this.state?.foes ?? []).flatMap((foe, place) => (foe && foe.hp > 0 ? [place] : []));
  }

  /**
   * Place dont le joueur doit choisir l'action : un remplaçant à envoyer d'abord, sinon la première place qu'il commande,
   * occupée et sans action ce tour (null : il n'a plus rien à choisir).
   */
  private nextPlace(snapshot: BattleSnapshot): number | null {
    if (snapshot.mustSwitch.length) return snapshot.mustSwitch[0] ?? null;
    return snapshot.controlled.find((place) => this.allyAt(place) && !snapshot.chosen.includes(place)) ?? null;
  }

  private showActions(snapshot: BattleSnapshot): void {
    const isDuel = snapshot.mode === 'duel';
    const isTrainer = snapshot.mode === 'trainer';
    const place = this.nextPlace(snapshot);
    const double = snapshot.allies.length > 1;
    if (place === null) {
      // Plus rien à choisir : on attend l'adversaire (duel) ou le partenaire (coop).
      const awaited = snapshot.waiting ?? snapshot.opponent ?? 'l\'adversaire';
      this.waiting.replaceChildren(...withIcon('hourglass', `En attente de ${awaited}…`));
      this.waiting.hidden = false;
      this.showUndo(snapshot, null);
      return;
    }
    this.place = place;
    this.waiting.hidden = true;
    this.actions.hidden = false;
    if (double) this.stage.highlight({ target: 'ally', place });
    this.showUndo(snapshot, place);

    const active = this.allyAt(place);
    const canSwitch = snapshot.team.some((monster) => monster.hp > 0 && !snapshot.allies.some((ally) => ally?.monster.id === monster.id));
    this.renderMoves(snapshot, place);
    for (const button of $$<HTMLButtonElement>('.other button', this.actions)) {
      const act = button.dataset.act;
      button.hidden = (isDuel && (act === 'capture' || act === 'bag')) || (isTrainer && (act === 'capture' || act === 'flee'));
      button.disabled = (act === 'switch' && !canSwitch) || (act === 'capture' && !this.targets().length) || (!active && act !== 'switch');
      if (act === 'flee') button.replaceChildren(...(isDuel ? withIcon('flag', 'Abandonner') : withIcon('flee', 'Fuir')));
    }
    if (snapshot.mustSwitch.length) this.showSwitchChoices(true);
  }

  /**
   * Combat double : le monstre qui attend son ordre (« Que doit faire Brindille ? ») et, si un autre a déjà reçu le
   * sien ce tour, de quoi revenir dessus.
   */
  private showUndo(snapshot: BattleSnapshot, place: number | null): void {
    const double = snapshot.allies.length > 1;
    const undoable = snapshot.mustSwitch.length ? [] : snapshot.chosen.filter((chosen) => snapshot.controlled.includes(chosen));
    const previous = undoable.at(-1);
    const parts: (Node | string)[] = [];
    if (previous !== undefined) {
      const back = el('button', { className: 'secondary undo' }, ...withIcon('undo', allyMonster(snapshot.allies[previous])?.name ?? 'Retour'));
      back.title = 'Revenir sur l\'action déjà choisie';
      back.addEventListener('click', () => this.send({ type: 'undo', place: previous }));
      parts.push(back);
    }
    const name = place === null ? null : this.allyAt(place)?.name;
    if (double && name && !snapshot.mustSwitch.length) parts.push(el('span', {}, `Que doit faire ${name} ?`));
    this.prompt.replaceChildren(...parts);
    this.prompt.hidden = !parts.length;
  }

  /** Les quatre attaques du monstre de cette place (type, puissance, énergie), ou Lutte s'il n'en connaît aucune. */
  private renderMoves(snapshot: BattleSnapshot, place: number): void {
    const ally = snapshot.allies[place];
    const active = this.allyAt(place);
    const known = (active?.moves ?? []).flatMap((id, slot) => {
      const move = this.catalog.get(id);
      return move ? [{ move, slot }] : [];
    });
    const energy = ally?.energy ?? 0;
    // L'indice d'efficacité vise le seul adversaire debout ; face à deux, il s'affiche au choix de la cible.
    const targets = this.targets();
    const foe = targets.length === 1 ? (snapshot.foes[targets[0]!] ?? null) : null;
    const buttons = known.map(({ move, slot }) => {
      const affordable = move.energy <= energy;
      const details = [move.category === 'damage' ? `Puiss. ${move.power}` : 'Statut', move.accuracy === null ? 'ne rate jamais' : `Préc. ${move.accuracy} %`,
        ...move.effects.map(describeEffect)].join(' · ');
      const hint = foe && active ? this.effectivenessHint(move, active, foe) : null;
      const button = el('button', {
        className: `${affordable ? '' : 'empty-pp'} ${hint?.className ?? ''}`, disabled: !affordable || !active,
        title: `${creatureText(move.description)}\n${details}${hint && foe ? `\nContre ${foe.name} (${elementTypes.get(foe.element).label}) : ${hint.detail}` : ''}`,
      }, el('strong', { textContent: move.name }),
      el('span', { className: 'pp', title: affordable ? `Coûte ${move.energy} d'énergie` : `Il faut ${move.energy} d'énergie (${energy} restante)` }, icon('energy'), ` ${move.energy}`),
      el('small', {}, icon(elementTypes.get(move.element).icon), ` ${elementTypes.get(move.element).label} · ${move.category === 'damage' ? `Puiss. ${move.power}` : 'Statut'}`),
      ...(hint ? [el('span', { className: 'efficacy' }, ...hint.label)] : []));
      button.dataset.act = 'move';
      button.dataset.slot = String(slot);
      button.style.setProperty('--c', elementTypes.get(move.element).color);
      return button;
    });
    if (!known.length && active) {
      const struggle = el('button', { className: 'struggle', title: 'Attaque désespérée, gratuite, qui blesse aussi le lanceur.' },
        el('strong', { textContent: 'Lutte' }), el('small', { textContent: creatureText('{Ce monstre} ne connaît aucune attaque') }));
      struggle.dataset.act = 'move';
      struggle.dataset.slot = '-1';
      buttons.push(struggle);
    }
    // Repos : à la place d'une attaque, le monstre récupère une part de son énergie.
    const full = energy >= ENERGY_MAX;
    const gain = Math.min(ENERGY_MAX - energy, restEnergy());
    const rest = el('button', {
      className: 'rest', disabled: full || !active,
      title: full ? 'Déjà plein d\'énergie' : `Renoncer à attaquer ce tour pour récupérer ${gain} d'énergie (touche R)`,
    }, el('strong', {}, ...withIcon('rest', 'Repos')), el('span', { className: 'pp' }, ...(full ? ['plein'] : [`+${gain} `, icon('energy')])),
    el('small', {}, `Récupère ${restEnergy()} `, icon('energy'), ' au lieu d\'attaquer'));
    rest.dataset.act = 'rest';
    // Les cases vides complètent les attaques, avant le Repos (toute la largeur) : jamais de rangée vide en dessous.
    this.moveList.replaceChildren(...buttons, ...emptyMoveSlots(MAX_MOVES - buttons.length), rest);
  }

  /**
   * Efficacité d'une attaque de dégâts contre un monstre d'en face (type de l'attaque contre son type),
   * avec le bonus de même type : de quoi choisir la bonne attaque sans connaître la table par cœur.
   */
  private effectivenessHint(move: Move, user: OwnedMonsterView, foe: Typed): { label: (Node | string)[]; detail: string; className: string } | null {
    if (move.category !== 'damage') return null;
    const type = effectivenessAgainst(move.element, foe);
    const stab = typesOf(user).includes(move.element);
    const total = type * (stab ? SAME_TYPE_BONUS : 1);
    const parts = [
      type > 1 ? `super efficace ×${Math.round(type * 100) / 100}` : type < 1 ? `peu efficace ×${Math.round(type * 100) / 100}` : 'efficacité normale',
      ...(stab ? [`même type que ${user.name} ×${SAME_TYPE_BONUS}`] : []),
    ];
    const label = [
      ...(type > 1 ? withIcon('arrow-up', 'Super efficace') : type < 1 ? withIcon('arrow-down', 'Peu efficace') : ['Normal']),
      ...(stab ? [' · ', icon('star', { label: 'Même type' })] : []),
    ];
    return { label, detail: `${parts.join(', ')} (×${Math.round(total * 100) / 100} au total)`, className: type > 1 ? 'strong' : type < 1 ? 'weak' : 'neutral' };
  }

  private onActionClick(event: Event): void {
    const button = (event.target as Element).closest<HTMLButtonElement>('button[data-act]');
    if (!button || !this.state) return;
    this.sounds.play('select');
    const { place } = this;
    switch (button.dataset.act) {
      case 'move': return this.pickTarget(Number(button.dataset.slot), (target) => this.send({ type: 'move', place, slot: Number(button.dataset.slot), target }));
      case 'rest': return this.send({ type: 'rest', place });
      case 'flee': return this.send({ type: 'flee', place });
      case 'switch': return this.showSwitchChoices(false);
      case 'capture': return this.showCapsuleChoices();
      case 'bag': return this.showBagChoices();
    }
  }

  /**
   * Cible de l'attaque `slot` (ou d'une capsule, `slot` null) : choisie parmi les adversaires debout s'ils sont plusieurs,
   * d'office sinon. Une attaque qui ne vise que son lanceur n'a pas de cible à choisir.
   */
  private pickTarget(slot: number | null, then: (target: number) => void): void {
    const targets = this.targets();
    const user = this.allyAt(this.place);
    const move = slot === null || slot < 0 ? null : this.catalog.get(user?.moves[slot] ?? -1) ?? null;
    const selfOnly = move !== null && move.category === 'status' && !move.effects.some((effect) => effect.type === 'status' || effect.type === 'flinch' || (effect.type === 'stat' && effect.target === 'target'));
    if (targets.length <= 1 || selfOnly) return then(targets[0] ?? 0);
    this.showChoices(targets.map((target) => {
      const foe = this.state?.foes[target];
      const hint = foe && move && user ? this.effectivenessHint(move, user, foe) : null;
      return {
        label: foe?.name ?? '?',
        detail: foe ? `Nv ${foe.level} · ${Math.round((100 * foe.hp) / Math.max(1, foe.maxHp))} % PV` : '',
        image: foe?.image ?? '',
        ...(hint ? { tone: { className: hint.className, title: hint.detail } } : {}),
        onPick: () => then(target),
      };
    }), false);
  }

  private count(item: keyof Inventory['items']): number {
    return this.inventory?.items[item] ?? 0;
  }

  private showCapsuleChoices(): void {
    const { place } = this;
    // Seules les capsules possédées : les autres s'achètent à la boutique.
    this.showChoices(itemCatalog.captures().filter((item) => this.count(item.id) > 0).map((item) => ({
      label: item.name,
      item,
      detail: `× ${this.count(item.id)}`,
      onPick: () => this.pickTarget(null, (target) => this.send({ type: 'capture', place, item: item.id, target })),
    })), false, 'Plus de capsules : achetez-en à la boutique.');
  }

  private showBagChoices(): void {
    const healable = (this.state?.team ?? []).some(canHeal);
    this.showChoices(itemCatalog.heals().map((item) => ({
      label: item.name,
      item,
      detail: `× ${this.count(item.id)} · +${item.effect.amount} PV`,
      disabled: this.count(item.id) < 1 || !healable,
      onPick: () => this.showHealTargets(item),
    })), false, healable ? 'Votre sac est vide.' : 'Votre équipe est en pleine forme.');
  }

  /** Les fiches des monstres sur le terrain, PV du combat compris (ceux de l'équipe peuvent dater du tour d'avant). */
  private teamNow(): OwnedMonsterView[] {
    const { team = [], allies = [] } = this.state ?? {};
    return team.map((monster) => allies.find((ally) => ally?.monster.id === monster.id)?.monster ?? monster);
  }

  /** Le soin choisi, à qui le donner : un monstre sur le terrain ou un autre de l'équipe (pas un K.O.). */
  private showHealTargets(item: HealItem): void {
    const { place } = this;
    const onField = new Set((this.state?.allies ?? []).flatMap((ally) => (ally ? [ally.monster.id] : [])));
    this.showChoices(this.teamNow().map((monster) => ({
      label: onField.has(monster.id) ? `${monster.name} (au combat)` : monster.name,
      detail: monster.hp <= 0 ? 'K.O.' : `${monster.hp}/${monster.maxHp} PV${canHeal(monster) ? ` → +${Math.min(item.effect.amount, monster.maxHp - monster.hp)}` : ''}`,
      image: monster.image,
      disabled: !canHeal(monster),
      onPick: () => this.send({ type: 'item', place, item: item.id, monsterId: monster.id }),
    })), false);
  }

  private showSwitchChoices(forced: boolean): void {
    const { place } = this;
    const onField = new Set((this.state?.allies ?? []).flatMap((ally) => (ally && ally.monster.hp > 0 ? [ally.monster.id] : [])));
    const foes = (this.state?.foes ?? []).filter((foe) => foe !== null && foe.hp > 0) as WildMonsterView[];
    this.showChoices(this.teamNow().map((monster) => ({
      label: monster.name,
      detail: `Nv ${monster.level} · ${monster.hp}/${monster.maxHp} PV`,
      image: monster.image,
      ...this.strongMovesHint(monster, foes),
      disabled: monster.hp <= 0 || onField.has(monster.id),
      onPick: () => this.send({ type: 'switch', place, id: monster.id }),
    })), forced);
  }

  /** Indice du changement de monstre : il connaît une attaque de dégâts super efficace contre un monstre d'en face. */
  private strongMovesHint(monster: OwnedMonsterView, foes: readonly (Typed & { name: string })[]): Pick<Choice, 'hint'> {
    const lines = foes.flatMap((foe) => {
      const strong = monster.moves.flatMap((id) => {
        const move = this.catalog.get(id);
        return move && move.category === 'damage' && effectivenessAgainst(move.element, foe) > 1 ? [move.name] : [];
      });
      return strong.length ? [`Super efficace contre ${foe.name} : ${strong.join(', ')}`] : [];
    });
    if (!lines.length) return {};
    return { hint: { label: [icon('arrow-up', { label: 'Attaque super efficace' })], title: lines.join('\n') } };
  }

  /** Sous-menu (changement de monstre, capsules, potions) à la place des actions. */
  private showChoices(options: Choice[], forced: boolean, emptyHint = ''): void {
    const buttons: HTMLElement[] = options.map((option) => {
      const button = el('button', { className: `choice-item ${option.tone?.className ?? ''}`, disabled: option.disabled ?? false });
      if (option.image) button.append(el('img', { src: option.image, alt: '' }));
      const hint = option.hint ? [' ', el('span', { className: 'choice-hint-icon', title: option.hint.title }, ...option.hint.label)] : [];
      button.append(el('span', {}, ...(option.item ? [itemVisual(option.item), ' '] : []), option.label, ...hint), el('small', { textContent: option.detail ?? '' }));
      const tip = option.hint?.title ?? option.tone?.title;
      if (tip) button.title = tip;
      button.addEventListener('click', () => option.onPick());
      return button;
    });
    if (emptyHint && options.every((option) => option.disabled)) buttons.unshift(el('p', { className: 'choice-hint', textContent: emptyHint }));
    if (!forced) {
      const back = el('button', { className: 'secondary', textContent: 'Retour' });
      back.addEventListener('click', () => {
        this.choices.hidden = true;
        this.actions.hidden = false;
      });
      buttons.push(back);
    }
    this.choices.replaceChildren(...buttons);
    this.choices.hidden = false;
    this.actions.hidden = true;
  }

  /** Envoie l'action ; les boutons restent masqués jusqu'à la fin de la séquence de réponse. */
  private send(action: BattleAction): void {
    this.lockActions();
    this.handlers.onAction(action);
  }
}
